import alertTriangleIcon from 'assets/alert-triangle.svg';
import analyticsIcon from 'assets/analytics.svg';
import flagIcon from 'assets/flag-icon.svg';
import membersIconPair from 'assets/members-icon-pair.svg';
import openBookIcon from 'assets/open-book.svg';

export const REVIEW_CATEGORIES = [
  {
    key: 'ux_critique',
    title: 'UX Critique',
    description: 'Streamline UX and identify usability issues for any flow',
    badge: '🧠',
    disabled: false,
  },
  {
    key: 'onboarding_flow',
    title: 'User Onboarding',
    description: 'Uncover UX insights to boost activations',
    badge: '⛳',
    disabled: false,
  },
];

const ONBOARDING_CHIPS = [
  {
    label: 'Simplify Flow',
    text: 'Evaluate the signup/onboarding flow for unnecessary steps and cognitive burden.'
      + ' Identify which steps can be removed, combined, or deferred to create a faster path to value.',
  },
  {
    label: 'First-time UX',
    text: 'Review the onboarding experience for new users. Identify where first-time users struggle'
      + ' to understand value or get started, and suggest clearer introduction flows.',
  },
  {
    label: 'Uncover Features',
    text: 'Analyze feature discoverability issues. Identify why users are missing key features and'
      + ' suggest ways to improve feature visibility and onboarding.',
  },
  {
    label: 'Build Trust',
    text: 'Evaluate elements that impact user trust and confidence. Identify areas where the interface'
      + ' feels unreliable or unprofessional and suggest trust-building improvements.',
  },
];

const UX_CRITIQUE_CHIPS = [
  {
    label: 'Clarify Actions',
    text: 'Review this interface for clarity issues. Identify where users might be confused about'
      + ' functionality, next steps, or purpose. Suggest ways to make actions more obvious.',
  },
  {
    label: 'Fix Drop-Offs',
    text: 'Analyze this user flow for friction points, abandonment triggers, and logical sequence issues.'
      + ' Identify where users get confused or drop off and suggest improvements.',
  },
  {
    label: 'Improve Messaging',
    text: 'Evaluate how the value proposition and pricing are presented. Identify confusion points and'
      + ' suggest clearer ways to communicate benefits and cost structure.',
  },
  {
    label: 'Tidy Layout',
    text: 'Assess content structure and visual hierarchy. Identify areas where information feels overwhelming or'
      + ' poorly organized and suggest better grouping/prioritization.',
  },
];

export const CHIP_OPTIONS_BY_CATEGORY = {
  ux_critique: UX_CRITIQUE_CHIPS,
  onboarding_flow: ONBOARDING_CHIPS,
};

const AI_NATIVE_EXPERIENCE_TEXT = `Description: Identify opportunities to transform existing flows into AI-native \
experiences that reduce user effort and modernize interactions for an AI-first world.

Evaluation Cues:
Manual steps AI could automate
Interactions that could scale with AI assistance
Moments for smart suggestions or recommendations
Conversational interfaces replacing form-based flows`;

const BUSINESS_GOAL_UX_CRITIQUE_OPTIONS = [
  {
    label: 'Increase conversion rate',
    text: `Description: Raise the rate of desired actions like signups, upgrades, or purchases. \
Remove objections and friction that prevent users from committing.

Evaluation Cues:
Frictionless forms
Value propositions clarity
Objection handlers
Trust signals placement
Pricing transparency`,
  },
  {
    label: 'Improve task completion',
    text: `Description: Help users successfully complete intended actions without confusion or errors. \
Focus on eliminating friction that prevents goal completion.

Evaluation Cues:
CTA clarity and prominence
Flow logic and sequence
Affordances and visual cues
Error prevention and recovery
Feedback confirmation`,
  },
  {
    label: 'Reduce cognitive load',
    text: `Description: Make the interface easier to scan, understand, and decide in. \
Minimize mental effort required to process information and take action.

Evaluation Cues:
Hierarchy and visual organization
Information density and grouping
Pattern consistency
Progressive disclosure
White space and breathing room`,
  },
  {
    label: 'Improve value clarity',
    text: `Description: Ensure users understand what they get and why it matters. \
Bridge the gap between features shown and benefits perceived.

Evaluation Cues:
Value prop messaging
Pricing clarity and context
Benefit articulation
Use case examples
Feature explanations`,
  },
  {
    label: 'Move to AI native experience',
    text: AI_NATIVE_EXPERIENCE_TEXT,
  },
];

const BUSINESS_GOAL_ONBOARDING_OPTIONS = [
  {
    label: 'Increase activation rate',
    text: `Description: Get more signups to their "aha moment" where they experience core value. \
Focus on the first meaningful outcome that hooks users.

Evaluation Cues:
Minimal steps shown upfront
Quick wins previewed early
Value demonstration speed
Setup friction reduction
First success celebration`,
  },
  {
    label: 'Improve completion rate',
    text: `Description: Maximize the percentage of users who finish signup and setup without dropping off. \
Remove friction points that cause abandonment.

Evaluation Cues:
Step count minimized
Unnecessary fields removed
Progress indicators clear
Exit points identified
Abandonment triggers addressed`,
  },
  {
    label: 'Reduce time to value',
    text: `Description: Get users to their first meaningful outcome as fast as possible. \
Cut perceived steps and defer non-essential setup to post-activation.

Evaluation Cues:
Critical path streamlined
Deferrable elements identified
Non-essential gates removed
Speed perception managed
Value delivery prioritized`,
  },
  {
    label: 'Build user confidence',
    text: `Description: Make users feel safe to proceed, give data, and invest time. \
Address anxiety and credibility concerns that cause early abandonment.

Evaluation Cues:
Privacy and security cues
Credibility indicators
Professional visual polish
Risk-reversal messaging
Transparency signals`,
  },
  {
    label: 'Move to AI native experience',
    text: AI_NATIVE_EXPERIENCE_TEXT,
  },
];

export const BUSINESS_GOAL_OPTIONS_BY_CATEGORY = {
  ux_critique: BUSINESS_GOAL_UX_CRITIQUE_OPTIONS,
  onboarding_flow: BUSINESS_GOAL_ONBOARDING_OPTIONS,
};

export const MAX_UPLOAD_FILES = 20;

export const MAX_INPUT_LENGTH = 700;

export const MIN_PERSONA_INPUT_LENGTH = 140;

// The problem statement holds richer, multi-sentence context (a slash-menu
// template plus the user's own detail), so it gets more room than the other
// context fields.
export const FIELD_MAX_LENGTHS = {
  title: MAX_INPUT_LENGTH,
  audience: MAX_INPUT_LENGTH,
  problem: 1000,
  businessGoal: MAX_INPUT_LENGTH,
  observedAnalytics: MAX_INPUT_LENGTH,
};

export const sanitizeInputText = (text, limit = MAX_INPUT_LENGTH) => (text || '').slice(0, limit);

// Ask Anything feature constants
export const ASK_ANYTHING_PROMPTS = [
  {
    id: 'overview',
    icon: openBookIcon,
    label: 'Give me an overview',
    prompt: 'Give me an overview',
  },
  {
    id: 'goals',
    icon: flagIcon,
    label: 'How does this tie to our goals?',
    prompt: 'How does this audit connect to our business goals? Which findings'
      + ' matter most for what we\'re trying to achieve?',
  },
  {
    id: 'prioritize',
    icon: analyticsIcon,
    label: 'Prioritize these findings',
    prompt: 'Rank these findings by priority: what should we tackle first, and why?',
  },
  {
    id: 'impact',
    icon: membersIconPair,
    label: 'What\'s the impact on users?',
    prompt: 'What\'s the impact of these findings on users? Which ones affect'
      + ' their experience the most, and how?',
  },
  {
    id: 'risk',
    icon: alertTriangleIcon,
    label: 'Identify the steps with high risk',
    prompt: 'Which of these findings carry the most risk or uncertainty if we'
      + ' act on them, and what should we watch out for?',
  },
];

export const ASK_ANYTHING_TABS = {
  FINDINGS: 'findings',
  ASK_AUDIT: 'ask_audit',
};

export const SORT_MODES = {
  IMPACT: 'impact',
  EFFORT: 'effort',
};

export const IMPACT_GROUPS = [
  { key: 'new_idea', label: 'New idea' },
  { key: 'high_impact', label: 'High impact' },
  { key: 'low_impact', label: 'Low impact' },
  { key: 'positive', label: 'Positive' },
];

export const EFFORT_GROUPS = [
  { key: 'quick_win', label: 'Quick win' },
  { key: 'new_idea', label: 'New idea' },
  { key: 'positive', label: 'Positive' },
];
